import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Save, Loader2, Plus, Star } from "lucide-react";

const API = "/api";

const TestimonialsEditor = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", location: "", text: "", rating: 5, savings: "" });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/testimonials`);
      setItems(res.data);
    } catch { toast.error("Errore caricamento"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const startNew = () => {
    setEditing("new");
    setForm({ name: "", location: "", text: "", rating: 5, savings: "" });
  };

  const startEdit = (t) => {
    setEditing(t.id);
    setForm({ name: t.name, location: t.location || "", text: t.text || "", rating: t.rating || 5, savings: t.savings || "" });
  };

  const handleSave = async () => {
    if (!form.name || !form.text) { toast.error("Nome e testo sono obbligatori"); return; }
    setSaving(true);
    try {
      if (editing === "new") {
        await axios.post(`${API}/admin/testimonials`, form);
        toast.success("Recensione creata!");
      } else {
        await axios.put(`${API}/admin/testimonials/${editing}`, form);
        toast.success("Recensione aggiornata!");
      }
      setEditing(null);
      fetchData();
    } catch { toast.error("Errore nel salvataggio"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/testimonials/${id}`);
      toast.success("Recensione eliminata");
      fetchData();
    } catch { toast.error("Errore eliminazione"); }
  };

  const toggleStatus = async (t) => {
    const newStatus = t.status === "active" ? "hidden" : "active";
    try {
      await axios.put(`${API}/admin/testimonials/${t.id}`, { status: newStatus });
      toast.success(newStatus === "active" ? "Recensione attivata" : "Recensione nascosta");
      fetchData();
    } catch { toast.error("Errore"); }
  };

  if (editing) return (
    <div data-testid="testimonial-editor">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-600">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
          {editing === "new" ? "Nuova Recensione" : "Modifica Recensione"}
        </h2>
      </div>
      <div className="bg-white rounded-xl border border-slate-100 p-6 max-w-xl space-y-4">
        <div>
          <label className="text-xs font-semibold text-slate-500 mb-1 block">Nome Cliente *</label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Mario R." className="h-10 rounded-xl" data-testid="testimonial-name" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500 mb-1 block">Localita</label>
          <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
            placeholder="Milano, Lombardia" className="h-10 rounded-xl" data-testid="testimonial-location" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500 mb-1 block">Testo Recensione *</label>
          <textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })}
            rows={4} placeholder="La mia esperienza con Fotovoltaico Chiavi in Mano..."
            className="w-full rounded-xl border border-input px-3 py-2 text-sm resize-none" data-testid="testimonial-text" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-slate-500 mb-1 block">Valutazione (1-5)</label>
            <div className="flex gap-1" data-testid="testimonial-rating">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} onClick={() => setForm({ ...form, rating: n })}
                  className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${n <= form.rating ? "bg-amber-100 text-amber-500" : "bg-slate-50 text-slate-300"}`}>
                  <Star className="w-4 h-4 fill-current" />
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 mb-1 block">Risparmio annuo</label>
            <Input value={form.savings} onChange={(e) => setForm({ ...form, savings: e.target.value })}
              placeholder="1.200/anno" className="h-10 rounded-xl" data-testid="testimonial-savings" />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-3">
          <button onClick={() => setEditing(null)}
            className="text-sm font-semibold text-slate-500 hover:text-slate-700 px-4 py-2">Annulla</button>
          <button onClick={handleSave} disabled={saving}
            className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-full px-6 py-2.5 text-sm flex items-center gap-2 disabled:opacity-50"
            data-testid="testimonial-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {editing === "new" ? "Crea" : "Salva"}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div data-testid="admin-testimonials">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
          Recensioni ({items.length})
        </h2>
        <button onClick={startNew}
          className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-full px-5 py-2.5 text-sm btn-scale flex items-center gap-2"
          data-testid="testimonial-create-btn">
          <Plus className="w-4 h-4" /> Nuova Recensione
        </button>
      </div>

      {loading ? (
        <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400 mx-auto" /></div>
      ) : items.length === 0 ? (
        <div className="py-12 text-center text-slate-400 text-sm">Nessuna recensione</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {items.map((t) => (
            <div key={t.id} className={`bg-white rounded-xl border p-5 transition-colors ${t.status === "active" ? "border-slate-100" : "border-amber-200 bg-amber-50/30"}`}
              data-testid={`testimonial-item-${t.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="font-semibold text-sm text-slate-900">{t.name}</div>
                  <div className="text-xs text-slate-400">{t.location}</div>
                </div>
                <span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-full ${t.status === "active" ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>
                  {t.status === "active" ? "Attiva" : "Nascosta"}
                </span>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed mb-3 line-clamp-3">"{t.text}"</p>
              <div className="flex items-center justify-between">
                <div className="flex gap-0.5">
                  {Array.from({ length: t.rating || 5 }).map((_, j) => (
                    <Star key={j} className="w-3 h-3 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                {t.savings && <span className="text-xs font-bold text-emerald-600">{t.savings}</span>}
              </div>
              <div className="flex items-center gap-2 mt-4 pt-3 border-t border-slate-50">
                <button onClick={() => startEdit(t)} className="text-xs font-semibold text-blue-600 hover:text-blue-700"
                  data-testid={`testimonial-edit-${t.id}`}>Modifica</button>
                <button onClick={() => toggleStatus(t)} className="text-xs font-semibold text-amber-600 hover:text-amber-700"
                  data-testid={`testimonial-toggle-${t.id}`}>
                  {t.status === "active" ? "Nascondi" : "Attiva"}
                </button>
                <button onClick={() => handleDelete(t.id)} className="text-xs font-semibold text-red-500 hover:text-red-600 ml-auto"
                  data-testid={`testimonial-delete-${t.id}`}>Elimina</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TestimonialsEditor;
